import React, { useState, useEffect, useCallback, useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { Card, Spin, Empty, Tag, Typography, Tooltip, App, Table } from "antd";
import { Activity, Shield, Target, TrendingUp, Wallet } from "lucide-react";
import apiService from "../../services/api";
import useTheme from "../../hooks/useTheme";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import { withOwnerUserId } from "../../utils/ownerScopeParams";
import {
    buildMultiGroupNetAssetsOption,
    buildMultiGroupVolumeTrendOption,
    buildGroupPeriodVolumeBarOption,
    buildGroupPeriodPnLBarOption,
    buildGroupHedgeEfficiencyBarOption,
} from "../../utils/hedgePanoramaCharts";
import { formatVolume, formatChartTooltipValue } from "../../utils/formatters";
import "./HedgePanoramaSection.css";

const { Text } = Typography;

/**
 * 在综合分析全景、交易数据全景、资金走势等页面嵌入：多对冲组组合净资产对比、各组区间总交易量与期末组合资金、区间盈亏、平均对冲效率。
 * 无 view_hedge_groups 权限时不渲染、不请求。
 */
const HedgePanoramaSection = ({
    dateFrom,
    dateTo,
    /** 管理员按用户查看时传入该用户 ID */
    ownerUserId = null,
    /** 与页面主 Spin 并行时可为 true，仅内部骨架 */
    quietLoading = false,
}) => {
    const { isDark } = useTheme();
    const { message: msg } = App.useApp();
    const [canView, setCanView] = useState(false);
    const [loading, setLoading] = useState(false);
    const [groupSeries, setGroupSeries] = useState([]);

    useEffect(() => {
        try {
            const u = JSON.parse(localStorage.getItem("user") || "{}");
            setCanView(hasPermission(u, PERMISSIONS.VIEW_HEDGE_GROUPS));
        } catch {
            setCanView(false);
        }
    }, []);

    const load = useCallback(async () => {
        if (!canView || !dateFrom || !dateTo) return;
        if (!quietLoading) setLoading(true);
        try {
            const listParams = withOwnerUserId({}, ownerUserId);
            const gr = await apiService.hedgeGroups.getGroups(listParams);
            const list = gr.data?.data || [];
            if (!list.length) {
                setGroupSeries([]);
                return;
            }
            const params = withOwnerUserId(
                { date_from: dateFrom, date_to: dateTo },
                ownerUserId,
            );
            const results = await Promise.all(
                list.map(async (g) => {
                    const tr = await apiService.hedgeGroups.getGroupTrends(g.id, params);
                    const payload = tr.data?.data;
                    return {
                        group: { id: g.id, name: g.name },
                        combined: payload?.combined || [],
                        analysis: payload?.analysis || {},
                        volumeTrend: payload?.volume_trend || [],
                    };
                }),
            );
            setGroupSeries(results);
        } catch (err) {
            if (err.response?.status !== 403) {
                msg.error(err.apiMessage || err.message || "加载对冲数据失败");
            }
            setGroupSeries([]);
        } finally {
            if (!quietLoading) setLoading(false);
        }
    }, [canView, dateFrom, dateTo, ownerUserId, quietLoading, msg]);

    useEffect(() => {
        load();
    }, [load]);

    const netAssetsOption = useMemo(
        () => buildMultiGroupNetAssetsOption({ seriesList: groupSeries, isDark }),
        [groupSeries, isDark],
    );
    const volumeTrendLineOption = useMemo(
        () => buildMultiGroupVolumeTrendOption({ seriesList: groupSeries, isDark }),
        [groupSeries, isDark],
    );
    const pnlBarOption = useMemo(
        () => buildGroupPeriodPnLBarOption({ seriesList: groupSeries, isDark }),
        [groupSeries, isDark],
    );
    const effBarOption = useMemo(
        () => buildGroupHedgeEfficiencyBarOption({ seriesList: groupSeries, isDark }),
        [groupSeries, isDark],
    );
    const volumeBarOption = useMemo(
        () => buildGroupPeriodVolumeBarOption({ seriesList: groupSeries, isDark }),
        [groupSeries, isDark],
    );

    const groupVolumeTableColumns = useMemo(
        () => [
            {
                title: "对冲组",
                dataIndex: "name",
                key: "name",
                ellipsis: true,
            },
            {
                title: (
                    <Tooltip title="所选日期范围内，组内各成员（account + 交易所 + 现货/合约）在 trade_volumes 中的成交量之和">
                        <span>区间总交易量</span>
                    </Tooltip>
                ),
                dataIndex: "volume",
                key: "volume",
                align: "right",
                width: 140,
                render: (v) => formatVolume(v ?? 0),
            },
            {
                title: (
                    <Tooltip title="与上方「组合净资产」折线一致：区间内最后一个双方均有权益快照的交易日，两账号净资产之和">
                        <span>期末组合资金</span>
                    </Tooltip>
                ),
                dataIndex: "netAssets",
                key: "netAssets",
                align: "right",
                width: 150,
                render: (v) => (v != null && !Number.isNaN(v) ? `${formatChartTooltipValue(v)} USDT` : "—"),
            },
        ],
        [],
    );

    const groupVolumeTableData = useMemo(
        () =>
            groupSeries.map((s, i) => ({
                key: s.group?.id ?? i,
                name: s.group?.name ?? `组${i + 1}`,
                volume: s.analysis?.period_total_volume ?? 0,
                netAssets:
                    (s.combined || []).length > 0
                        ? s.combined[s.combined.length - 1]?.net_assets
                        : s.analysis?.latest_combined_net_assets,
            })),
        [groupSeries],
    );

    const summary = useMemo(() => {
        let sumPnl = 0;
        let sumVol = 0;
        let effSum = 0;
        let effN = 0;
        groupSeries.forEach((s) => {
            sumPnl += s.analysis?.total_pnl ?? 0;
            sumVol += s.analysis?.period_total_volume ?? 0;
            const e = s.analysis?.avg_hedge_efficiency;
            if (e != null && !Number.isNaN(e)) {
                effSum += e;
                effN += 1;
            }
        });
        return {
            count: groupSeries.length,
            sumPnl,
            sumVol,
            avgEff: effN > 0 ? effSum / effN : null,
        };
    }, [groupSeries]);

    if (!canView) {
        return null;
    }

    const inner = (
        <div className="hedge-panorama-inner">
            <div className="hedge-panorama-summary">
                <Tag icon={<Shield size={12} />} color="geekblue">
                    {summary.count} 个对冲组
                </Tag>
                <Tag icon={<Activity size={12} />} color="blue">
                    区间总交易量合计 {formatVolume(summary.sumVol)}
                </Tag>
                <Tag
                    icon={<TrendingUp size={12} />}
                    color={summary.sumPnl >= 0 ? "success" : "error"}
                >
                    区间组合盈亏合计 {summary.sumPnl >= 0 ? "+" : ""}
                    {summary.sumPnl.toLocaleString(undefined, { minimumFractionDigits: 2 })} USDT
                </Tag>
                {summary.avgEff != null && (
                    <Tag icon={<Target size={12} />} color="processing">
                        平均对冲效率 {summary.avgEff.toFixed(1)}%
                    </Tag>
                )}
                <Tooltip title="交易量来自 trade_volumes（含按日走势）；资金与盈亏来自权益快照。两账号同日均有快照时才有组合净值点。">
                    <Text type="secondary" className="hedge-panorama-hint">
                        数据说明
                    </Text>
                </Tooltip>
            </div>

            {groupSeries.length > 0 && (
                <Card size="small" variant="borderless" className="hedge-panorama-chart-card">
                    <div className="hedge-panorama-chart-head">
                        <Text strong>每组对冲：区间总交易量与期末组合资金</Text>
                        <Tooltip title="总交易量 = 组内各成员在 trade_volumes 中的成交量之和（与所选日期范围一致）">
                            <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                                （两腿成交量合计）
                            </Text>
                        </Tooltip>
                    </div>
                    <Table
                        size="small"
                        pagination={false}
                        columns={groupVolumeTableColumns}
                        dataSource={groupVolumeTableData}
                        locale={{ emptyText: "暂无数据" }}
                    />
                </Card>
            )}

            {groupSeries.length === 0 && !loading ? (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="暂无对冲组，可在「对冲监控」中创建配对"
                />
            ) : (
                <>
                    <Card size="small" variant="borderless" className="hedge-panorama-chart-card">
                        <div className="hedge-panorama-chart-head">
                            <Activity size={16} />
                            <Text strong>各组每日总交易量走势</Text>
                            <Tooltip title="每个交易日：组内各成员在 trade_volumes 中的成交量之和；所选范围内无成交的日期为 0（折线可能贴底）。">
                                <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                                    两腿日成交量合计
                                </Text>
                            </Tooltip>
                        </div>
                        {volumeTrendLineOption ? (
                            <ReactECharts option={volumeTrendLineOption} style={{ height: 320, width: "100%" }} notMerge lazyUpdate />
                        ) : (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="所选区间内暂无成交量数据（trade_volumes）"
                                style={{ margin: "40px 0" }}
                            />
                        )}
                    </Card>

                    <Card size="small" variant="borderless" className="hedge-panorama-chart-card">
                        <div className="hedge-panorama-chart-head">
                            <Wallet size={16} />
                            <Text strong>各组组合净资产走势对比</Text>
                        </div>
                        {netAssetsOption ? (
                            <ReactECharts option={netAssetsOption} style={{ height: 360, width: "100%" }} notMerge lazyUpdate />
                        ) : (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="所选区间内暂无完整组合权益点（需两成员同日数据）"
                                style={{ margin: "48px 0" }}
                            />
                        )}
                    </Card>

                    <div className="hedge-panorama-bar-grid hedge-panorama-bar-grid--triple">
                        <Card size="small" variant="borderless" className="hedge-panorama-chart-card">
                            <div className="hedge-panorama-chart-head">
                                <Text strong>区间总交易量</Text>
                            </div>
                            {volumeBarOption ? (
                                <ReactECharts option={volumeBarOption} style={{ height: 280, width: "100%" }} notMerge lazyUpdate />
                            ) : (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无数据" />
                            )}
                        </Card>
                        <Card size="small" variant="borderless" className="hedge-panorama-chart-card">
                            <div className="hedge-panorama-chart-head">
                                <Text strong>区间累计组合盈亏</Text>
                            </div>
                            {pnlBarOption ? (
                                <ReactECharts option={pnlBarOption} style={{ height: 280, width: "100%" }} notMerge lazyUpdate />
                            ) : (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无数据" />
                            )}
                        </Card>
                        <Card size="small" variant="borderless" className="hedge-panorama-chart-card">
                            <div className="hedge-panorama-chart-head">
                                <Text strong>平均对冲效率</Text>
                            </div>
                            {effBarOption ? (
                                <ReactECharts option={effBarOption} style={{ height: 280, width: "100%" }} notMerge lazyUpdate />
                            ) : (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无数据" />
                            )}
                        </Card>
                    </div>
                </>
            )}
        </div>
    );

    if (quietLoading) {
        return inner;
    }

    return <Spin spinning={loading}>{inner}</Spin>;
};

export default HedgePanoramaSection;
